import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Code2,
  Sparkles,
  ChevronRight,
  Shield,
  Zap,
  Cloud,
  Globe,
  ArrowRight,
  Command,
  Laptop,
  Blocks,
  Infinity,
} from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';

const FEATURES = [
  {
    icon: <Shield className="w-5 h-5" />,
    title: 'End-to-End Encryption',
    description: 'Your code snippets are encrypted using industry-standard AES encryption before being stored, ensuring maximum security and privacy for your sensitive data.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: <Cloud className="w-5 h-5" />,
    title: 'Cloud Sync',
    description: 'Access your code snippets from any device, anytime. Your data is automatically synced across all your devices in real-time, with offline support coming soon.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: <Globe className="w-5 h-5" />,
    title: 'Share Snippets',
    description: 'Share your code snippets with teammates or the community. Control visibility with public and private options, and track views on your shared snippets.',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: <Blocks className="w-5 h-5" />,
    title: 'Smart Organization',
    description: 'Keep your code snippets organized with custom tags and folders. Our intelligent tagging system helps you find the right code quickly when you need it.',
    gradient: 'from-blue-500 to-cyan-500',
  },
];

const HomePage = () => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 border-b bg-background/80 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <Code2 className="w-6 h-6 text-primary group-hover:text-primary/80 transition-colors" />
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-600">
              CodeNest
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/auth">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-16">
        {/* Hero Section */}
        <section className="min-h-[90vh] relative flex items-center">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:32px_32px]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[40rem] w-[40rem] bg-purple-500/30 rounded-full blur-[128px] animate-pulse" />
          </div>

          <div className="container mx-auto px-4 py-20 relative">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 bg-muted/50 border px-4 py-2 rounded-full text-sm"
              >
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span>Your code deserves a beautiful home</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-6xl md:text-7xl font-bold leading-tight"
              >
                Code Organization
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-primary">
                  Reimagined
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-muted-foreground max-w-2xl mx-auto"
              >
                A modern platform for developers to store, organize, and share code snippets.
                Built with elegance and simplicity in mind.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground">
                    Start Coding
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="secondary"
                  className="w-full sm:w-auto hover:bg-secondary/80"
                  onClick={scrollToFeatures}
                >
                  Learn More
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-32 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-background to-background" />

          <div className="container mx-auto px-4 relative">
            <div className="max-w-2xl mx-auto text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 bg-muted/50 border px-4 py-2 rounded-full text-sm mb-8"
              >
                <Zap className="w-4 h-4 text-primary" />
                <span>Powerful Features</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold mb-4"
              >
                Everything you need
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-muted-foreground"
              >
                Powerful features to help you manage your code snippets effectively
              </motion.p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {FEATURES.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative overflow-hidden rounded-2xl border bg-card p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />
                  <div className="relative">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-6`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why CodeNest Section */}
        <section className="py-32 relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-16 items-center" style={{ display: 'grid', justifyContent: 'center' }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <h2 className="text-4xl font-bold text-center md:text-left">
                  Why choose{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-primary">
                    CodeNest
                  </span>
                  ?
                </h2>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Laptop className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Modern Interface</h3>
                      <p className="text-muted-foreground">
                        A beautiful, intuitive interface that makes code organization a joy
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                      <Blocks className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Smart Organization</h3>
                      <p className="text-muted-foreground">
                        Powerful tagging and folder system to keep your code organized
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                      <Infinity className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Limitless Possibilities</h3>
                      <p className="text-muted-foreground">
                        Store any type of code with syntax highlighting and markdown support
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 justify-center md:justify-start">
                  <Link to="/register">
                    <Button size="lg">Get Started</Button>
                  </Link>
                  <Link to="/pricing">
                    <Button size="lg" variant="outline">View Plans</Button>
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 to-primary/30 rounded-3xl blur-3xl" />
                <img
                  src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop"
                  alt="Code Editor"
                  className="relative rounded-3xl border shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-32 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-background" />

          <div className="container mx-auto px-4 relative">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold mb-6"
              >
                Ready to organize your
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-primary">
                  code snippets?
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground mb-8"
              >
                Join thousands of developers who trust CodeNest
                <br />
                to manage their programming knowledge.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/register">
                  <Button size="lg" className="group">
                    Start Organizing
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline">
                    View Pricing
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;